import React, { useCallback, useRef, useState } from 'react'
import documentAccessAPI from 'features/documentAccess/documentAccessAPI'
import organisationService from 'services/organisationService'
import Menu from '@mui/material/Menu'
import styled from 'styled-components'
import MenuItem from '@mui/material/MenuItem'
import Divider from '@mui/material/Divider'
import PersonOutlineIcon from '@mui/icons-material/PersonOutline'
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone'
import ApartmentOutlinedIcon from '@mui/icons-material/ApartmentOutlined'
import LockOpenOutlinedIcon from '@mui/icons-material/LockOpenOutlined'
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined'
import LogoutIcon from '@mui/icons-material/Logout'
import { useDispatch, useSelector } from 'react-redux'
import { getCurrentUserRole, getUserPhotoUrl, getCurrentUser } from 'features/auth/ducks/selectors'
import InitialsAvatar from 'features/navigation/components/InitialsAvatar'
import Avatar from 'components/Avatar'
import { PRIVATE_PATHS, redirect } from 'utils/paths'
import { logoutUserRoutine } from 'features/auth/ducks/actions'
import { pathOr } from 'ramda'
import { hideEmail } from 'utils/user'

const AvatarMenu = () => {
  const dispatch = useDispatch()
  const [anchorEl, setAnchorEl] = useState(null)
  const open = Boolean(anchorEl)
  const userPhotoUrl = useSelector(getUserPhotoUrl)
  const logoutUser = useCallback(() => dispatch(logoutUserRoutine()), [dispatch])
  const userRole = useSelector(getCurrentUserRole)
  const isFrsUser = userRole === 'emergency_service'
  const isResponsiblePerson = userRole === 'responsible_person'
  const canSeeAccessRequests = userRole === 'user'
  const user = useSelector(getCurrentUser)
  const currentUserSource = pathOr('', ['source', 'value'], user)
  const isFromInfoExchange = currentUserSource === 'info_exchange'

  const [accessWaiting, setAccessWaiting] = useState(0)
  const [isSuperAdmin, setIsSuperAdmin] = useState(false)
  const [organisationName, setOrganisationName] = useState('')
  const fullName = [user?.firstName, user?.lastName].filter(Boolean).join(' ')
  const superAdminChecked = useRef(false)

  const openMenu = (event) => {
    setAnchorEl(event.currentTarget)
    if (canSeeAccessRequests) {
      documentAccessAPI.summary()
        .then(res => setAccessWaiting(res?.data?.data?.waitingForYou || 0))
        .catch(() => setAccessWaiting(0))
    }
    if (!superAdminChecked.current) {
      superAdminChecked.current = true
      organisationService.getMine()
        .then(res => {
          setIsSuperAdmin(!!res?.data?.data?.isSuperAdmin)
          setOrganisationName(res?.data?.data?.name || '')
        })
        .catch(() => setIsSuperAdmin(false))
    }
  }
  const handleClose = () => setAnchorEl(null)

  const handleItemClick = path => () => {
    handleClose()
    redirect(path)
  }

  return (
    <div>
      <Menu
        keepMounted
        anchorEl={anchorEl}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        open={open}
        onClose={handleClose}
        slotProps={{ paper: { sx: menuPaperSx } }}
        MenuListProps={{ sx: { py: 0.75 }, 'aria-label': 'Account' }}
      >
        <MenuHeader>
          <HeaderName>{fullName || 'Your account'}</HeaderName>
          {user?.email && <HeaderEmail>{hideEmail(user.email)}</HeaderEmail>}
          {isSuperAdmin && <HeaderRole>Super Admin{organisationName ? ` of ${organisationName}` : ''}</HeaderRole>}
        </MenuHeader>
        <Divider sx={{ my: 0.75 }} />
        <MenuItem onClick={handleItemClick(PRIVATE_PATHS.profileSettings)} sx={itemSx}>
          <ItemIcon><PersonOutlineIcon /></ItemIcon>
          <ItemLabel>My profile</ItemLabel>
        </MenuItem>
        <MenuItem onClick={handleItemClick(PRIVATE_PATHS.notificationPreferences)} sx={itemSx}>
          <ItemIcon><NotificationsNoneIcon /></ItemIcon>
          <ItemLabel>Emails and notifications</ItemLabel>
        </MenuItem>
        {isSuperAdmin && (
          <MenuItem onClick={handleItemClick(PRIVATE_PATHS.organisation)} sx={itemSx}>
            <ItemIcon><ApartmentOutlinedIcon /></ItemIcon>
            <ItemLabel>Your organisation</ItemLabel>
          </MenuItem>
        )}
        {canSeeAccessRequests && (
          <MenuItem onClick={handleItemClick(PRIVATE_PATHS.documentAccessRequests)} sx={itemSx}>
            <ItemIcon><LockOpenOutlinedIcon /></ItemIcon>
            <ItemLabel>
              Document access requests
              {accessWaiting > 0 && <CountPill>{accessWaiting} awaiting approval</CountPill>}
            </ItemLabel>
          </MenuItem>
        )}
        {!isFrsUser && !isResponsiblePerson && (
          <MenuItem onClick={handleItemClick(PRIVATE_PATHS.billingList)} sx={itemSx}>
            <ItemIcon><ReceiptLongOutlinedIcon /></ItemIcon>
            <ItemLabel>Billing list</ItemLabel>
          </MenuItem>
        )}
        <Divider sx={{ my: 0.75 }} />
        <MenuItem onClick={logoutUser} sx={itemSx}>
          <ItemIcon><LogoutIcon /></ItemIcon>
          <ItemLabel>Log out</ItemLabel>
        </MenuItem>
      </Menu>
      <AvatarWrapper
        type='button'
        onClick={openMenu}
        aria-label='Account menu'
        aria-haspopup='menu'
        aria-expanded={open}
      >
        {
          userPhotoUrl && !isFromInfoExchange
            ? (
              <ImageAvatar
                src={userPhotoUrl}
                firstName={user?.firstName}
                lastName={user?.lastName}
                email={user?.email}
                size={40}
              />
              )
            : <InitialsAvatar />
        }
      </AvatarWrapper>
    </div>
  )
}

export default AvatarMenu

const menuPaperSx = {
  mt: 1,
  width: 300,
  maxWidth: 'calc(100vw - 32px)',
  borderRadius: '12px',
  border: '1px solid #e6eaec',
  boxShadow: '0 6px 20px rgba(20, 35, 45, 0.10)'
}

const itemSx = {
  minHeight: 44,
  mx: 0.75,
  px: 1.25,
  borderRadius: '8px',
  gap: 1.5,
  fontSize: 15,
  color: '#1f2a30',
  '&:hover': { backgroundColor: '#f0fbfc' },
  '&.Mui-focusVisible': { backgroundColor: '#e3f7f9' }
}

const MenuHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 10px 18px 8px;
`

const HeaderName = styled.span`
  font-size: 16px;
  font-weight: 600;
  color: #1f2a30;
`

const HeaderEmail = styled.span`
  font-size: 14px;
  color: #5b6870;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const HeaderRole = styled.span`
  align-self: flex-start;
  margin-top: 6px;
  padding: 2px 10px;
  border-radius: 999px;
  background: #e3f7f9;
  color: #0b6a74;
  font-size: 13px;
  font-weight: 600;
`

const ItemIcon = styled.span`
  display: flex;
  align-items: center;
  color: #5b6870;

  svg {
    font-size: 20px;
  }
`

const ItemLabel = styled.span`
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
  white-space: normal;
`

const CountPill = styled.span`
  padding: 2px 8px;
  border-radius: 999px;
  background: #fef3c7;
  color: #8a5300;
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
`

const AvatarWrapper = styled.button`
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 40px;
  min-height: 40px;
  padding: 0;
  border: 0;
  border-radius: 50%;
  background: none;
  font: inherit;

  &:focus-visible {
    outline: 2px solid #33becc;
    outline-offset: 2px;
  }

  & > div, & > img {
    width: 40px;
    height: 40px;
    min-width: 40px;
    min-height: 40px;
    font-size: 16px;
  }

  @media (max-width: 768px) {
    width: 40px;
    height: 40px;
  }
`

const ImageAvatar = styled(Avatar)`
  @media (max-width: 768px) {
    width: 36px;
    height: 36px;
  }
`
